import { useCallback, useEffect } from 'react';

import {
  useValidatePasswordChange,
  useConfirmPasswordChange,
} from 'api/requests';
import { ErrorCode } from 'api/types/error';
import { useObjectState } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { useDialog } from 'libs/ui/Dialog/useDialog';

import {
  ChangePasswordFormSubmit,
  FormErrors,
} from '../forms/changePasswordForm';

export const useChangePassword = () => {
  const notify = useNotify();
  const { t } = useTranslation();

  const {
    isOpen: isOpenVerificationDialog,
    onClose: onCloseVerificationDialog,
    open: openVerificationDialog,
  } = useDialog();

  const {
    validatePasswordChange,
    loading: validateLoading,
    error: validateError,
  } = useValidatePasswordChange();
  const { confirmPasswordChange, error: confirmError } =
    useConfirmPasswordChange();

  const [formError, setFormError] = useObjectState<FormErrors>({});

  const onSubmit: ChangePasswordFormSubmit = useCallback(
    async (values, helpers) => {
      if (!values.otpCode) {
        const res = await validatePasswordChange({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        });

        if (res) {
          openVerificationDialog();
        }
      } else {
        const res = await confirmPasswordChange({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
          otpToken: values.otpCode,
        });
        if (res) {
          onCloseVerificationDialog();
          helpers.resetForm();
          notify.info(t('common.successSave'));
        }
      }
    },
    [
      t,
      notify,
      confirmPasswordChange,
      onCloseVerificationDialog,
      openVerificationDialog,
      validatePasswordChange,
    ]
  );

  const handleOnCloseVerificationDialog = () => {
    onCloseVerificationDialog();
    setFormError({ otpCode: undefined });
  };

  useEffect(() => {
    if (validateError || confirmError) {
      if (confirmError?.code === ErrorCode.INVALID_OTP_TOKEN) {
        setFormError({
          oldPassword: undefined,
          newPassword: undefined,
          otpCode: confirmError.message,
        });
      } else if (validateError?.code === ErrorCode.INCORRECT_PASSWORD) {
        setFormError({
          oldPassword: validateError.message,
          newPassword: undefined,
          otpCode: undefined,
        });
      } else if (validateError?.code === ErrorCode.OLD_PASSWORD) {
        setFormError({
          newPassword: validateError.message,
          oldPassword: undefined,
          otpCode: undefined,
        });
      } else {
        setFormError({
          oldPassword: undefined,
          otpCode: undefined,
          newPassword: undefined,
        });
        notify.error(validateError);
      }
    } else {
      setFormError({
        oldPassword: undefined,
        otpCode: undefined,
        newPassword: undefined,
      });
    }
  }, [validateError, notify, setFormError, confirmError]);

  return {
    isOpenVerificationDialog,
    submitLoading: validateLoading,
    formError,
    onSubmit,
    onCloseVerificationDialog: handleOnCloseVerificationDialog,
  };
};
